import { createSlice, PayloadAction } from '@reduxjs/toolkit';

/**
 * Tiempo por defecto (en milisegundos) para cerrar automáticamente las notificaciones
 */
export const NOTIFICATION_AUTO_CLOSE_DURATION = 5000;

export type NotificationType = 'success' | 'error' | 'warning' | 'info' | 'important';

export interface Notification {
  id: string;
  type: NotificationType;
  message: string;
  duration?: number;
  timestamp: number;
}

export interface Modal {
  id: string;
  component: string;
  props?: Record<string, unknown>;
  isClosable?: boolean;
}

interface UIState {
  sidebarOpen: boolean;
  sidePanel?: {
    open: boolean;
    title?: string;
    /** Tooltip text shown next to the title via an info icon (e.g. why a record is read-only). */
    titleTooltip?: string;
    /** Optional heading shown above the titleTooltip body. */
    titleTooltipTitle?: string;
    component?: string;
    props?: Record<string, unknown>;
    isLoading?: boolean;
  };
  loading: {
    global: boolean;
    components: Record<string, boolean>;
  };
  notifications: Notification[];
  modals: Modal[];
}

const initialState: UIState = {
  sidebarOpen: false,
  sidePanel: { open: false },
  loading: {
    global: false,
    components: {},
  },
  notifications: [],
  modals: [],
};

export const uiSlice = createSlice({
  name: 'ui',
  initialState,
  reducers: {
    openSidePanel: (
      state,
      action: PayloadAction<{
        title?: string;
        component?: string;
        props?: Record<string, unknown>;
      }>,
    ) => {
      state.sidePanel = { open: true, ...action.payload, isLoading: false };
    },
    closeSidePanel: (state) => {
      if (state.sidePanel) {
        state.sidePanel.open = false;
        state.sidePanel.isLoading = false;
      }
    },
    setSidePanelLoading: (state, action: PayloadAction<boolean>) => {
      if (state.sidePanel) {
        state.sidePanel.isLoading = action.payload;
      }
    },
    setSidePanelTitleTooltip: (
      state,
      action: PayloadAction<{ body: string; title?: string } | null>,
    ) => {
      if (state.sidePanel) {
        state.sidePanel.titleTooltip = action.payload?.body ?? undefined;
        state.sidePanel.titleTooltipTitle = action.payload?.title ?? undefined;
      }
    },
    // Notification actions
    addNotification: (state, action: PayloadAction<Omit<Notification, 'id' | 'timestamp'>>) => {
      const notification: Notification = {
        ...action.payload,
        id: `notification-${Date.now()}-${Math.random().toString(36).substring(2, 11)}`,
        timestamp: Date.now(),
      };
      state.notifications.push(notification);
    },
    removeNotification: (state, action: PayloadAction<string>) => {
      state.notifications = state.notifications.filter((n) => n.id !== action.payload);
    },
    clearNotifications: (state) => {
      state.notifications = [];
    },

    // Modal actions
    openModal: (state, action: PayloadAction<Omit<Modal, 'id'>>) => {
      const modal: Modal = {
        ...action.payload,
        id: `modal-${Date.now()}-${Math.random().toString(36).substring(2, 11)}`,
      };
      state.modals.push(modal);
    },
    closeModal: (state, action: PayloadAction<string>) => {
      state.modals = state.modals.filter((m) => m.id !== action.payload);
    },
    closeLastModal: (state) => {
      state.modals.pop();
    },
    closeAllModals: (state) => {
      state.modals = [];
    },
  },
});

export const {
  addNotification,
  removeNotification,
  clearNotifications,
  openModal,
  closeModal,
  closeLastModal,
  closeAllModals,
  openSidePanel,
  closeSidePanel,
  setSidePanelLoading,
  setSidePanelTitleTooltip,
} = uiSlice.actions;

export default uiSlice.reducer;
